import { updateI18nLocale } from '@/utils/i18n'

// LocalStorage key
export const LANGUAGE_PREFERENCE_KEY = 'preferredLanguage';

// List of supported languages with all needed properties
export const LANGUAGES = [
  { label: 'English', value: 'english', locale: 'en', francCode: 'eng' },
  { label: 'हिन्दी (Hindi)', value: 'hindi', locale: 'hi', francCode: 'hin' },
  { label: 'বাংলা (Bengali)', value: 'bengali', locale: 'bn', francCode: 'ben' },
  { label: 'తెలుగు (Telugu)', value: 'telugu', locale: 'te', francCode: 'tel' },
  { label: 'தமிழ் (Tamil)', value: 'tamil', locale: 'ta', francCode: 'tam' },
  { label: 'मराठी (Marathi)', value: 'marathi', locale: 'mr', francCode: 'mar' },
  { label: 'ગુજરાતી (Gujarati)', value: 'gujarati', locale: 'gu', francCode: 'guj' },
  { label: 'ಕನ್ನಡ (Kannada)', value: 'kannada', locale: 'kn', francCode: 'kan' },
  { label: 'മലയാളം (Malayalam)', value: 'malayalam', locale: 'ml', francCode: 'mal' },
  { label: 'ਪੰਜਾਬੀ (Punjabi)', value: 'punjabi', locale: 'pa', francCode: 'pan' },
  { label: 'ଓଡ଼ିଆ (Odia)', value: 'odia', locale: 'or', francCode: 'ori' },
  { label: 'اُردُو (Urdu)', value: 'urdu', locale: 'ur', francCode: 'urd' }
];

// User language preference management
export function getCurrentLanguage() {
  return localStorage.getItem(LANGUAGE_PREFERENCE_KEY) || 'english';
}

export function setCurrentLanguage(preferredLanguage) {
  localStorage.setItem(LANGUAGE_PREFERENCE_KEY, preferredLanguage);
}

/**
 * Get the user's preferred language from localStorage
 * @returns {string} The preferred language code or null if not set
 */
export function getPreferredLanguage() {
  return localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
}

/**
 * Save the user's preferred language to localStorage
 * @param {string} languageCode - The language code to save
 */
export function savePreferredLanguage(languageCode) {
  localStorage.setItem(LANGUAGE_PREFERENCE_KEY, languageCode);
  
  // Update i18n locale directly without needing localeCode
  updateI18nLocale(languageCode);
  
  // Dispatch event
  window.dispatchEvent(new CustomEvent('language-changed', {
    detail: { language: languageCode }
  }));
}

/**
 * Check if the user has already set a language preference
 * @returns {boolean} True if language preference exists
 */
export function hasLanguagePreference() {
  return !!getPreferredLanguage();
}

/**
 * Get label for a language code
 * @param {string} languageCode - The language code
 * @returns {string} The human-readable language name
 */
export function getLanguageLabel(languageCode) {
  const language = LANGUAGES.find(lang => lang.value === languageCode);
  return language ? language.label : 'Unknown';
}

/**
 * Clear the user's preferred language setting
 */
export function clearPreferredLanguage() {
  localStorage.removeItem(LANGUAGE_PREFERENCE_KEY);
  
  // Optional: Dispatch a custom event that components can listen for
  window.dispatchEvent(new CustomEvent('language-changed', {
    detail: { language: null }
  }));
}

// Language conversion functions
export function getLocaleFromPreferred(preferredLanguage) {
  const language = LANGUAGES.find(lang => lang.value === preferredLanguage);
  return language ? language.locale : 'en'; // Default to English
}

export function getPreferredFromLocale(locale) {
  const language = LANGUAGES.find(lang => lang.locale === locale);
  return language ? language.value : 'english'; // Default to English
}

export function getFrancFromLocale(locale) {
  const language = LANGUAGES.find(lang => lang.locale === locale);
  return language ? language.francCode : 'eng'; // Default to English
}

export function getLocaleFromFranc(francCode) {
  const language = LANGUAGES.find(lang => lang.francCode === francCode);
  return language ? language.locale : 'en'; // Default to English
}